import { X, History, TrendingUp, TrendingDown, Package, Edit3, Trash2, Plus, Clock, ShoppingCart, UtensilsCrossed } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { EmptyState } from "./ui/EmptyState";
import { IconButton, Button } from "./ui/button";
import type { GlobalMovement } from "../types/designTypes";

interface GlobalHistoryProps {
  movements: GlobalMovement[];
  isOpen: boolean;
  onClose: () => void;
}

export function GlobalHistory({ movements, isOpen, onClose }: GlobalHistoryProps) {
  if (!isOpen) return null;

  const getIcon = (type: string) => {
    switch (type) {
      case "Modification":
        return <Edit3 className="w-4 h-4 text-[var(--color-steel)]" />;
      case "Suppression":
        return <Trash2 className="w-4 h-4 text-[var(--color-brick)]" />;
      case "Ajout":
        return <Plus className="w-4 h-4 text-[var(--color-olive)]" />;
      case "Ajustement quantité":
        return <TrendingUp className="w-4 h-4 text-[var(--color-amber)]" />;
      default:
        return <Clock className="w-4 h-4 text-[var(--muted-foreground)]" />;
    }
  };

  const getPageIcon = (page: GlobalMovement["page"]) => {
    switch (page) {
      case "Inventaire":
        return <Package className="w-3.5 h-3.5" />;
      case "Commande":
        return <ShoppingCart className="w-3.5 h-3.5" />;
      case "Recette":
        return <UtensilsCrossed className="w-3.5 h-3.5" />;
      case "Ventes":
        return <TrendingDown className="w-3.5 h-3.5" />;
    }
  };

  const getPageColor = (page: GlobalMovement["page"]) => {
    switch (page) {
      case "Inventaire":
        return "bg-[var(--color-steel)]/20 text-[var(--color-steel)] border border-[var(--color-steel)]/30";
      case "Commande":
        return "bg-[var(--color-amber)]/20 text-[var(--color-amber)] border border-[var(--color-amber)]/30";
      case "Recette":
        return "bg-[var(--color-olive)]/20 text-[var(--color-olive)] border border-[var(--color-olive)]/30";
      case "Ventes":
        return "bg-[var(--color-brick)]/20 text-[var(--color-brick)] border border-[var(--color-brick)]/30";
    }
  };

  const getTypeColor = (type: GlobalMovement["type"]) => {
    switch (type) {
      case "Ajout":
        return "bg-[var(--color-olive)]/20 text-[var(--color-olive)] border border-[var(--color-olive)]/30";
      case "Modification":
      case "Édition en masse":
        return "bg-[var(--color-steel)]/20 text-[var(--color-steel)] border border-[var(--color-steel)]/30";
      case "Suppression":
        return "bg-[var(--color-brick)]/20 text-[var(--color-brick)] border border-[var(--color-brick)]/30";
      case "Ajustement quantité":
        return "bg-[var(--color-amber)]/20 text-[var(--color-amber)] border border-[var(--color-amber)]/30";
      default:
        return "bg-[var(--muted-foreground)]/20 text-[var(--muted-foreground)] border border-[var(--muted-foreground)]/30";
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in">
      <div className="w-full max-w-4xl bg-[var(--background)] rounded-xl shadow-2xl border border-[var(--border)] animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--border)]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[var(--color-steel-dark)]/20 rounded-lg">
              <Clock className="w-5 h-5 text-[var(--color-steel)]" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-[var(--foreground)]">Historique global</h2>
              <p className="text-sm text-[var(--muted-foreground)]">{movements.length} mouvement(s) enregistré(s)</p>
            </div>
          </div>
          <IconButton
            icon={X}
            variant="ghost"
            onClick={onClose}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {movements.length === 0 ? (
            <EmptyState
              icon={History}
              title="Aucun mouvement enregistré"
              description="Il n'y a pas de mouvements enregistrés pour le moment."
            />
          ) : (
            <div className="space-y-3">
              {movements.map((movement) => (
                <div
                  key={movement.id}
                  className="bg-[var(--muted)] rounded-lg p-4 hover:bg-[var(--switch-background)] transition-colors border border-[var(--border)]"
                >
                  <div className="flex items-start gap-4">
                    <div className="mt-1">{getIcon(movement.type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <h3 className="font-semibold text-[var(--foreground)]">{movement.itemNom}</h3>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getPageColor(
                                movement.page
                              )}`}
                            >
                              {getPageIcon(movement.page)}
                              {movement.page}
                            </span>
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTypeColor(
                                movement.type
                              )}`}
                            >
                              {movement.type}
                            </span>
                          </div>
                        </div>
                        <span className="text-xs text-[var(--muted-foreground)] whitespace-nowrap">
                          {format(movement.timestamp, "d MMM yyyy à HH:mm", { locale: fr })}
                        </span>
                      </div>
                      <p className="text-sm text-[var(--muted-foreground)] mb-2">{movement.details}</p>
                      {movement.ancienneValeur && movement.nouvelleValeur && (
                        <div className="flex items-center gap-2 text-xs">
                          <span className="px-2 py-1 bg-[var(--color-brick)]/20 text-[var(--color-brick)] rounded border border-[var(--color-brick)]/30">
                            {movement.ancienneValeur}
                          </span>
                          <span className="text-[var(--muted-foreground-light)]">→</span>
                          <span className="px-2 py-1 bg-[var(--color-olive)]/20 text-[var(--color-olive)] rounded border border-[var(--color-olive)]/30">
                            {movement.nouvelleValeur}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[var(--border)]">
          <Button
            variant="primary"
            onClick={onClose}
            className="w-full"
          >
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
}